import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import { Button } from "@progress/kendo-react-buttons";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  FITBookingCancel,
  getFITBookingDetailsForCancellation,
} from "./services/FITBooking.services";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";

const FITBookingCancellation: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const TourBookingNo = location.state?.TourBookingNo;
  const PackageBookingID = location.state?.PackageBookingID;
  const [formKey, setFormKey] = useState(1);
  const loading = useAppSelector((state) => state.FITBooking.loading);
  const FITBookingDetailForCancellation = useAppSelector(
    (state) => state.FITBooking.FITBookingDetailForCancellation
  );

  useEffect(() => {
    if (TourBookingNo) {
      dispatch(getFITBookingDetailsForCancellation(+TourBookingNo));
    }
  }, [TourBookingNo]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [FITBookingDetailForCancellation]);

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        PackageBookingID: PackageBookingID,
        Adult: values?.Adult,
        CNB: values?.CNB,
        CWB: values?.CWB,
        CancelledAmount: values?.CancelledAmount,
        CancelledRemarks: values?.CancelledRemarks,
      };
      const response = await dispatch(FITBookingCancel(payload));
      if (response?.meta?.requestStatus === "fulfilled") {
        navigate(-1);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={FITBookingDetailForCancellation}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>FIT Booking Cancellation</Typography.h4>
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="TourBookingNo"
                    label="Tour Booking No"
                    placeholder="Tour Booking No"
                    component={FormTextField}
                    disabled={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Name"
                    label="Name"
                    placeholder="Name"
                    component={FormTextField}
                    disabled={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="MobileNo"
                    label="Mobile No"
                    placeholder="Mobile No"
                    component={FormTextField}
                    disabled={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="TourStartDate"
                    label="Tour Start Date"
                    placeholder="Tour Start Date"
                    component={FormDatePicker}
                    disabled={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="TourEndDate"
                    label="Tour End Date"
                    placeholder="Tour End Date"
                    component={FormDatePicker}
                    disabled={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="NoOfNights"
                    label="No Of Nights"
                    placeholder="No Of Nights"
                    component={FormNumericTextField}
                    disabled={true}
                    max={50}
                    format="n0"
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="PickUpPlace"
                    label="Pickup Place"
                    placeholder="PickUp Place"
                    component={FormTextField}
                    disabled={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="DropPlace"
                    label="Drop Place"
                    placeholder="Drop Place"
                    component={FormTextField}
                    disabled={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <hr className="mb-0" />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Adult"
                    label="Adult"
                    placeholder="Adult"
                    component={FormNumericTextField}
                    validator={requiredValidator}
                    astrike={true}
                    max={50}
                    format="n0"
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="CNB"
                    label="CNB"
                    placeholder="CNB"
                    component={FormNumericTextField}
                    max={50}
                    format="n0"
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="CWB"
                    label="CWB"
                    placeholder="CWB"
                    component={FormNumericTextField}
                    max={50}
                    format="n0"
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="CancelledAmount"
                    label="Cancelled Amount"
                    placeholder="Cancelled Amount"
                    component={FormNumericTextField}
                    validator={requiredValidator}
                    astrike={true}
                    format="n0"
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="CancelledRemarks"
                    label="Cancelled Remarks"
                    placeholder="Cancelled Remarks"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                    marginTop: 20,
                  }}
                >
                  <div>
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"error"}
                      style={{ marginRight: 4 }}
                      onClick={() => navigate("/fitbooking")}
                    >
                      Cancel
                    </Button>
                    <ButtonWithLoading
                      label={"Cancel Booking"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                      themeColor="error"
                    />
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default FITBookingCancellation;
